// My Applications — rebuilt against the internal design system's "Listing
// Page" template (tabs-with-counts → filter bar → row cards → pagination),
// using this app's own tokens (--primary/--surface/--text/…) rather than the
// reference doc's own HireEdge brand palette, so the page still reads as
// part of ps-career-frontend (My Profile / My Skills / Open Positions), not
// a visually separate product.
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CalendarClock,
  ChevronLeft,
  ChevronRight,
  Inbox,
  Loader2,
  RefreshCcw,
  Search,
} from 'lucide-react';
import { useInterestRequests, useInterviews } from 'src/hooks/useMyPipelines';
import {
  cleanJobTitle,
  fmtDateShort,
  fmtInterviewWhen,
  humanize,
  interestStatusPill,
} from './myApplications.helpers';

const PAGE_SIZE_OPTIONS = [10, 20, 50];

// ── Metadata row — plain text joined by small dot separators (§5.18) ───────
function MetaRow({ items }) {
  const parts = items.filter(Boolean);
  if (!parts.length) return null;
  return (
    <div
      className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs"
      style={{ color: 'var(--text-faint)' }}
    >
      {parts.map((part, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <React.Fragment key={i}>
          {i > 0 && (
            <span
              className="h-[3px] w-[3px] flex-shrink-0 rounded-full"
              style={{ background: 'var(--border)' }}
              aria-hidden="true"
            />
          )}
          <span>{part}</span>
        </React.Fragment>
      ))}
    </div>
  );
}

// ── Row card shell (§5.18) — hover-elevates, left = info, right = action.
// Opens the same detail view the magic link uses (just authenticated, via
// :secMappingId instead of a token). ──
function RowCard({ to, actionLabel = 'View', children }) {
  return (
    <li
      className="group overflow-hidden rounded-[var(--radius-lg)] border transition-all duration-150"
      style={{
        background: 'var(--surface)',
        borderColor: 'var(--border-light)',
        boxShadow: 'var(--shadow-xs)',
      }}
    >
      <style>{`
        .ma-row:hover { transform: translateY(-2px); }
      `}</style>
      <Link
        to={to}
        className="ma-row flex flex-col gap-3 p-4 transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:p-5"
        style={{ '--tw-ring-color': 'var(--ring)' }}
        onMouseEnter={(e) => {
          e.currentTarget.parentElement.style.borderColor = 'var(--primary)';
          e.currentTarget.parentElement.style.boxShadow = 'var(--shadow-md)';
          e.currentTarget.style.background = 'var(--primary-wash)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.parentElement.style.borderColor =
            'var(--border-light)';
          e.currentTarget.parentElement.style.boxShadow = 'var(--shadow-xs)';
          e.currentTarget.style.background = 'transparent';
        }}
      >
        <div className="min-w-0 flex-1">{children}</div>
        <div className="flex flex-shrink-0 items-center justify-end gap-3">
          <span
            className="inline-flex flex-shrink-0 items-center gap-1 rounded-[var(--radius-sm)] border px-3.5 py-1.5 text-xs font-semibold transition-colors group-hover:border-[var(--primary)] group-hover:text-[var(--primary)]"
            style={{ borderColor: 'var(--border)', color: 'var(--text-sec)' }}
          >
            {actionLabel}
            <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
          </span>
        </div>
      </Link>
    </li>
  );
}

function InterestRequestCard({ item }) {
  const pill = interestStatusPill(item);
  const title = cleanJobTitle(item.job_title) || 'Untitled role';
  const contacted = fmtDateShort(item.contacted_at);
  const acknowledged = fmtDateShort(item.acknowledged_at);

  return (
    <RowCard
      to={`/my-applications/requests/${item.sec_mapping_id}`}
      actionLabel={pill.label === 'Interested' ? 'Update Details' : 'View'}
    >
      <p
        className="truncate text-sm font-semibold"
        style={{ color: 'var(--text)', fontFamily: 'var(--font-display)' }}
      >
        {title}
      </p>
      <p className="mt-0.5 text-xs" style={{ color: 'var(--text-sec)' }}>
        {item.organization || 'Ahedge'}
      </p>
      <MetaRow
        items={[
          item.location_name,
          item.employment_type,
          item.work_mode && humanize(item.work_mode),
        ]}
      />
      {(contacted || acknowledged) && (
        <p className="mt-1.5 text-xs" style={{ color: 'var(--text-faint)' }}>
          Contacted {contacted}
          {acknowledged ? ` · Responded ${acknowledged}` : ''}
        </p>
      )}
    </RowCard>
  );
}

// Small calendar-style date tile — not part of the reference doc, but there's
// no equivalent for "interview scheduling" content in it; day/date at a
// glance is the one piece of scannability an interview list genuinely needs.
function DateTile({ start }) {
  if (!start) {
    return (
      <div
        className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-[var(--radius-md)]"
        style={{
          background: 'var(--surface-muted)',
          color: 'var(--text-faint)',
        }}
      >
        <CalendarClock className="h-5 w-5" aria-hidden="true" />
      </div>
    );
  }
  const weekday = new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
  }).format(start);
  const day = new Intl.DateTimeFormat(undefined, { day: 'numeric' }).format(
    start
  );
  return (
    <div
      className="flex h-11 w-11 flex-shrink-0 flex-col items-center justify-center rounded-[var(--radius-md)] border"
      style={{
        background: 'var(--primary-light)',
        borderColor: 'var(--primary-wash)',
      }}
    >
      <span
        className="text-[9px] font-semibold uppercase leading-tight"
        style={{ color: 'var(--primary)' }}
      >
        {weekday}
      </span>
      <span
        className="text-sm font-bold leading-tight"
        style={{ color: 'var(--primary)', fontFamily: 'var(--font-display)' }}
      >
        {day}
      </span>
    </div>
  );
}

function InterviewCard({ item }) {
  const title = cleanJobTitle(item.job_title) || 'Untitled role';
  const start = item.start_time ? new Date(item.start_time) : null;
  const { time } = fmtInterviewWhen(item.start_time, item.end_time);

  return (
    <RowCard to={`/my-applications/interviews/${item.sec_mapping_id}`}>
      <div className="flex min-w-0 gap-3">
        <DateTile
          start={start && !Number.isNaN(start.getTime()) ? start : null}
        />
        <div className="min-w-0">
          <p
            className="truncate text-sm font-semibold"
            style={{
              color: 'var(--text)',
              fontFamily: 'var(--font-display)',
            }}
          >
            {title}
          </p>
          <p className="mt-0.5 text-xs" style={{ color: 'var(--text-sec)' }}>
            {item.organization || 'Ahedge'}
            {item.round ? ` · ${item.round}` : ''}
          </p>
          <MetaRow items={[time, item.format]} />
        </div>
      </div>
    </RowCard>
  );
}

// ── Tabs with counts (§5.13 "Standard tabs") ────────────────────────────────
function TabsBar({ tabs, active, onChange }) {
  return (
    <div
      className="mb-4 flex items-center gap-1 border-b"
      style={{ borderColor: 'var(--border-light)' }}
      role="tablist"
    >
      {tabs.map((t) => {
        const isActive = t.value === active;
        return (
          <button
            key={t.value}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(t.value)}
            className="relative flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors"
            style={{
              color: isActive ? 'var(--primary)' : 'var(--text-faint)',
              fontWeight: isActive ? 600 : 500,
            }}
          >
            {t.label}
            {t.count != null && (
              <span
                className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1.5 text-[11px] font-semibold"
                style={{
                  background: isActive
                    ? 'var(--primary-light)'
                    : 'var(--surface-muted)',
                  color: isActive ? 'var(--primary)' : 'var(--text-faint)',
                }}
              >
                {t.count}
              </span>
            )}
            <span
              className="absolute inset-x-0 -bottom-px h-0.5 rounded-full transition-opacity"
              style={{
                background: 'var(--primary)',
                opacity: isActive ? 1 : 0,
              }}
              aria-hidden="true"
            />
          </button>
        );
      })}
    </div>
  );
}

// ── Filter bar (§5.20) — clear, search ───────────────────────────────────────
function FilterBar({ search, onSearchChange, hasActiveFilter, onClear }) {
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2.5">
      {hasActiveFilter && (
        <button
          type="button"
          onClick={onClear}
          className="text-xs font-medium underline-offset-2 hover:underline"
          style={{ color: 'var(--text-faint)' }}
        >
          Clear
        </button>
      )}

      <div className="relative ml-auto w-full sm:w-[240px]">
        <Search
          className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2"
          style={{ color: 'var(--text-faint)' }}
          aria-hidden="true"
        />
        <input
          type="text"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search by role or company"
          className="w-full rounded-[var(--radius-sm)] border py-2 pl-8 pr-3 text-xs focus:outline-none focus:ring-2"
          style={{
            borderColor: 'var(--border)',
            color: 'var(--text)',
            background: 'var(--surface)',
            '--tw-ring-color': 'var(--ring)',
          }}
        />
      </div>
    </div>
  );
}

// ── Pagination (§5.19) — rows-per-page, info text, numbered page buttons ───
function buildPageList(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const keep = new Set([
    1,
    2,
    total - 1,
    total,
    current - 1,
    current,
    current + 1,
  ]);
  const sorted = [...keep]
    .filter((p) => p >= 1 && p <= total)
    .sort((a, b) => a - b);
  const out = [];
  let prev = 0;
  sorted.forEach((p) => {
    if (prev && p - prev > 1) out.push(`gap-${p}`);
    out.push(p);
    prev = p;
  });
  return out;
}

function Pagination({ pagination, pageSize, onPageSizeChange, onPageChange }) {
  if (!pagination || !pagination.total) return null;
  const { page, total_pages: totalPages, total } = pagination;
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);
  const pageBtnBase =
    'flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-[var(--radius-sm)] border text-xs font-semibold transition-colors';

  return (
    <div
      className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t pt-4"
      style={{ borderColor: 'var(--border-light)' }}
    >
      <div className="flex items-center gap-2">
        <span className="text-xs" style={{ color: 'var(--text-faint)' }}>
          Rows per page
        </span>
        <select
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          className="rounded-[var(--radius-xs)] border px-1.5 py-1 text-xs"
          style={{
            borderColor: 'var(--border)',
            color: 'var(--text)',
            background: 'var(--surface)',
          }}
        >
          {PAGE_SIZE_OPTIONS.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </div>

      <span className="text-xs" style={{ color: 'var(--text-faint)' }}>
        {start}–{end} of {total}
      </span>

      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          aria-label="Previous page"
          className={pageBtnBase}
          style={{
            borderColor: 'var(--border)',
            color: 'var(--text-sec)',
            background: 'var(--surface)',
            opacity: page <= 1 ? 0.4 : 1,
          }}
        >
          <ChevronLeft className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
        {buildPageList(page, totalPages).map((p) =>
          typeof p === 'string' ? (
            <span
              key={p}
              className="px-0.5 text-xs"
              style={{ color: 'var(--text-faint)' }}
            >
              …
            </span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => onPageChange(p)}
              className={pageBtnBase}
              style={
                p === page
                  ? {
                      background: 'var(--primary)',
                      color: 'var(--primary-fg)',
                      borderColor: 'var(--primary)',
                    }
                  : {
                      borderColor: 'var(--border)',
                      color: 'var(--text-sec)',
                      background: 'var(--surface)',
                    }
              }
            >
              {p}
            </button>
          )
        )}
        <button
          type="button"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          aria-label="Next page"
          className={pageBtnBase}
          style={{
            borderColor: 'var(--border)',
            color: 'var(--text-sec)',
            background: 'var(--surface)',
            opacity: page >= totalPages ? 0.4 : 1,
          }}
        >
          <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

function SectionState({
  loading,
  isError,
  onRetry,
  empty,
  emptyIcon: EmptyIcon,
  emptyText,
}) {
  if (loading) {
    return (
      <div
        className="flex items-center gap-3 rounded-[var(--radius-md)] border p-6"
        style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}
      >
        <Loader2
          className="h-4 w-4 animate-spin"
          style={{ color: 'var(--primary)' }}
          aria-hidden="true"
        />
        <span className="text-sm" style={{ color: 'var(--text-muted)' }}>
          Loading…
        </span>
      </div>
    );
  }
  if (isError) {
    return (
      <div
        className="rounded-[var(--radius-md)] border p-6"
        style={{
          background: 'var(--destructive-light)',
          borderColor: 'var(--destructive)',
        }}
      >
        <p className="text-sm" style={{ color: 'var(--destructive)' }}>
          Something went wrong loading this list.
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-4 inline-flex items-center gap-2 rounded-[var(--radius-sm)] border px-3 py-1.5 text-sm font-medium transition-colors"
          style={{
            borderColor: 'var(--destructive)',
            color: 'var(--destructive)',
            background: 'var(--surface)',
          }}
        >
          <RefreshCcw className="h-3.5 w-3.5" aria-hidden="true" />
          Retry
        </button>
      </div>
    );
  }
  if (empty) {
    return (
      <div
        className="flex flex-col items-center gap-3 rounded-[var(--radius-lg)] border py-14 text-center"
        style={{
          background: 'var(--surface)',
          borderColor: 'var(--border-light)',
        }}
      >
        <div
          className="flex h-[72px] w-[72px] items-center justify-center rounded-full"
          style={{ background: 'var(--surface-muted)' }}
        >
          <EmptyIcon
            className="h-8 w-8"
            style={{ color: 'var(--text-faint)' }}
            aria-hidden="true"
          />
        </div>
        <p className="text-sm" style={{ color: 'var(--text-faint)' }}>
          {emptyText}
        </p>
      </div>
    );
  }
  return null;
}

export default function MyApplications() {
  const [tab, setTab] = useState('requests');
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [requestsPage, setRequestsPage] = useState(1);
  const [interviewsPage, setInterviewsPage] = useState(1);
  const [search, setSearch] = useState('');

  const requestsQuery = useInterestRequests({
    page: requestsPage,
    pageSize,
  });
  const interviewsQuery = useInterviews({
    page: interviewsPage,
    pageSize,
  });

  const requestItems = requestsQuery.data?.items || [];
  const requestPagination = requestsQuery.data?.pagination || null;
  const interviewItems = interviewsQuery.data?.items || [];
  const interviewPagination = interviewsQuery.data?.pagination || null;

  const tabs = [
    {
      value: 'requests',
      label: 'Interest Requests',
      count: requestPagination?.total,
    },
    {
      value: 'interviews',
      label: 'Interviews',
      count: interviewPagination?.total,
    },
  ];

  const isRequests = tab === 'requests';
  const activeItems = isRequests ? requestItems : interviewItems;

  const searchLower = search.trim().toLowerCase();
  const filteredItems = activeItems.filter((item) => {
    if (!searchLower) return true;
    const title = cleanJobTitle(item.job_title).toLowerCase();
    const org = String(item.organization || '').toLowerCase();
    return title.includes(searchLower) || org.includes(searchLower);
  });

  const hasActiveFilter = !!search;
  const clearFilters = () => setSearch('');

  const activeQuery = isRequests ? requestsQuery : interviewsQuery;
  const activePagination = isRequests ? requestPagination : interviewPagination;
  const setActivePage = isRequests ? setRequestsPage : setInterviewsPage;

  return (
    <div className="mx-auto w-full max-w-[1100px] px-4 pb-12 pt-6 text-left sm:px-7 sm:pt-8">
      <div className="mb-5">
        <h1
          className="text-xl font-bold tracking-tight sm:text-2xl"
          style={{
            fontFamily: 'var(--font-display)',
            color: 'var(--text)',
            letterSpacing: '-0.02em',
          }}
        >
          My Applications
        </h1>
        <p className="mt-1 text-sm" style={{ color: 'var(--text-muted)' }}>
          Roles that reached out to you, and any interviews booked off the back
          of them.
        </p>
      </div>

      <TabsBar
        tabs={tabs}
        active={tab}
        onChange={(next) => {
          setTab(next);
          setSearch('');
        }}
      />

      <FilterBar
        search={search}
        onSearchChange={setSearch}
        hasActiveFilter={hasActiveFilter}
        onClear={clearFilters}
      />

      <SectionState
        loading={activeQuery.isLoading}
        isError={activeQuery.isError}
        onRetry={activeQuery.refetch}
        empty={!activeQuery.isLoading && activeItems.length === 0}
        emptyIcon={isRequests ? Inbox : CalendarClock}
        emptyText={
          isRequests
            ? "No interest requests yet — we'll let you know when a role matches."
            : 'No interviews scheduled yet.'
        }
      />

      {!activeQuery.isLoading &&
        !activeQuery.isError &&
        activeItems.length > 0 &&
        (filteredItems.length > 0 ? (
          <ul className="flex flex-col gap-3">
            {filteredItems.map((item) =>
              isRequests ? (
                <InterestRequestCard key={item.sec_mapping_id} item={item} />
              ) : (
                <InterviewCard key={item.sec_mapping_id} item={item} />
              )
            )}
          </ul>
        ) : (
          <div
            className="rounded-[var(--radius-lg)] border py-10 text-center"
            style={{
              background: 'var(--surface)',
              borderColor: 'var(--border-light)',
            }}
          >
            <p className="text-sm" style={{ color: 'var(--text-faint)' }}>
              No results match your filter on this page.
            </p>
          </div>
        ))}

      <Pagination
        pagination={activePagination}
        pageSize={pageSize}
        onPageSizeChange={(n) => {
          setPageSize(n);
          setRequestsPage(1);
          setInterviewsPage(1);
        }}
        onPageChange={setActivePage}
      />
    </div>
  );
}
